import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Alert, App, Button, Card, Checkbox, Col, Form, Progress, Result, Row, Segmented, Select, Space, Table, Tag, Typography, Upload } from 'antd';
import { CalendarOutlined, DownloadOutlined, FileTextOutlined, InboxOutlined, TeamOutlined, UserDeleteOutlined } from '@ant-design/icons';
import { useQueryClient } from '@tanstack/react-query';
import dayjs from 'dayjs';
import {
  NOTES_IMPORT_BATCH,
  buildNoteBody,
  guessNotesColumns,
  parseSheetDate,
  type NotesImportColumns,
  type NotesImportFile,
  type NotesImportInput,
  type NotesImportMatch,
  type NotesImportResult,
} from '@crm/shared';
import { api, errorMessage } from '../../api/client';
import { PageHeader } from '../../components/PageHeader';
import { CardTitle, StatCard } from '../../components/StatCard';
import { fmtDate, plural } from '../../utils/format';

type Status = 'ready' | 'not-found' | 'no-email' | 'empty';

interface PreviewRow {
  key: number;
  /** spreadsheet row number, header being row 1 */
  line: number;
  email: string;
  body: string;
  /** the date as written, and as an instant (local noon, so it shows the same day in any time zone nearby) */
  rawDate: string;
  occurredAt: string | null;
  contact: NotesImportMatch | null;
  status: Status;
}

const STATUS: Record<Status, { label: string; color: string }> = {
  ready: { label: 'Ready', color: 'green' },
  'not-found': { label: 'No contact', color: 'orange' },
  'no-email': { label: 'No email', color: 'default' },
  empty: { label: 'Empty note', color: 'default' },
};

const csvCell = (v: string) => (/[",\n]/.test(v) ? `"${v.replace(/"/g, '""')}"` : v);

/**
 * Tools → Notes importer: reads a spreadsheet with one row per person (email, a summary, a last communication date),
 * adds each summary as a note on the matching contact's timeline, dated by that row, and moves the contact's Last
 * contacted forward to it. Contacts are matched by any of their emails; importing the same file again adds nothing.
 */
export function NotesImportPage() {
  const { message } = App.useApp();
  const qc = useQueryClient();
  const [file, setFile] = useState<NotesImportFile | null>(null);
  const [reading, setReading] = useState(false);
  const [readError, setReadError] = useState<string | null>(null);
  const [columns, setColumns] = useState<NotesImportColumns>({ email: null, note: null, date: null, extras: [] });
  const [matches, setMatches] = useState<Map<string, NotesImportMatch>>(new Map());
  const [updateLastContacted, setUpdateLastContacted] = useState(true);
  const [filter, setFilter] = useState<'all' | Status>('all');
  const [progress, setProgress] = useState<{ done: number; total: number } | null>(null);
  const [result, setResult] = useState<NotesImportResult | null>(null);

  const read = async (f: File) => {
    setReadError(null);
    setResult(null);
    setReading(true);
    try {
      const form = new FormData();
      form.append('file', f);
      const data = (await api.post<NotesImportFile>('/activities/notes-import/read', form, { headers: { 'Content-Type': 'multipart/form-data' }, timeout: 5 * 60_000 })).data;
      if (!data.rows.length) throw new Error('The file has no rows under its header.');
      const guess = guessNotesColumns(data.headers, data.rows);
      await loadMatches(data, guess.email);
      setColumns(guess);
      setFile(data);
      setFilter('all');
    } catch (e) {
      setFile(null);
      setReadError(errorMessage(e));
    } finally {
      setReading(false);
    }
  };

  const loadMatches = async (data: NotesImportFile, emailColumn: string | null) => {
    const emails = emailColumn ? [...new Set(data.rows.map((r) => (r[emailColumn] ?? '').trim().toLowerCase()).filter(Boolean))] : [];
    const found = emails.length ? (await api.post<NotesImportMatch[]>('/activities/notes-import/match', { emails })).data : [];
    setMatches(new Map(found.map((m) => [m.email, m])));
  };

  const setEmailColumn = async (email: string | null) => {
    setColumns((c) => ({ ...c, email, extras: c.extras.filter((h) => h !== email) }));
    if (!file) return;
    try {
      await loadMatches(file, email);
    } catch (e) {
      message.error(errorMessage(e));
    }
  };

  const rows = useMemo<PreviewRow[]>(() => {
    if (!file) return [];
    return file.rows.map((r, i) => {
      const email = columns.email ? (r[columns.email] ?? '').trim().toLowerCase() : '';
      const body = buildNoteBody(r, columns);
      const rawDate = columns.date ? (r[columns.date] ?? '').trim() : '';
      const day = parseSheetDate(rawDate);
      const occurredAt = day ? dayjs(new Date(day.year, day.month - 1, day.day, 12)).toISOString() : null;
      const contact = matches.get(email) ?? null;
      const status: Status = !email ? 'no-email' : !body ? 'empty' : contact ? 'ready' : 'not-found';
      return { key: i, line: i + 2, email, body, rawDate, occurredAt, contact, status };
    });
  }, [file, columns, matches]);

  const counts = useMemo(() => {
    const c = { ready: 0, 'not-found': 0, 'no-email': 0, empty: 0 } as Record<Status, number>;
    for (const r of rows) c[r.status]++;
    return {
      ...c,
      contacts: new Set(rows.filter((r) => r.status === 'ready').map((r) => r.contact!.contactId)).size,
      dated: rows.filter((r) => r.status === 'ready' && r.occurredAt).length,
      badDates: rows.filter((r) => r.rawDate && !r.occurredAt).length,
    };
  }, [rows]);

  const run = async () => {
    if (!file) return;
    const ready = rows.filter((r) => r.status === 'ready');
    const total: NotesImportResult = { added: 0, duplicates: 0, notFound: [] };
    setProgress({ done: 0, total: ready.length });
    try {
      for (let i = 0; i < ready.length; i += NOTES_IMPORT_BATCH) {
        const body: NotesImportInput = {
          type: 'NOTE',
          updateLastContacted,
          source: file.fileName,
          notes: ready.slice(i, i + NOTES_IMPORT_BATCH).map((r) => ({ email: r.email, body: r.body, occurredAt: r.occurredAt })),
        };
        const res = (await api.post<NotesImportResult>('/activities/notes-import', body)).data;
        total.added += res.added;
        total.duplicates += res.duplicates;
        total.notFound.push(...res.notFound);
        setProgress({ done: Math.min(i + NOTES_IMPORT_BATCH, ready.length), total: ready.length });
      }
      setResult(total);
      void qc.invalidateQueries({ queryKey: ['activities'] });
      void qc.invalidateQueries({ queryKey: ['contacts'] });
      void qc.invalidateQueries({ queryKey: ['contact'] });
    } catch (e) {
      // batches already sent stay; running it again skips the notes they added
      message.error(`Import stopped: ${errorMessage(e)}. Run it again to continue; notes already added are skipped.`);
      if (total.added || total.duplicates) setResult(total);
    } finally {
      setProgress(null);
    }
  };

  /** The rows that were not imported, as a CSV with the file's own columns plus the reason. */
  const downloadSkipped = () => {
    if (!file) return;
    const skipped = rows.filter((r) => r.status !== 'ready');
    const headers = ['Reason', ...file.headers];
    const lines = [headers, ...skipped.map((r) => [STATUS[r.status].label, ...file.headers.map((h) => file.rows[r.key][h] ?? '')])];
    const blob = new Blob([lines.map((l) => l.map(csvCell).join(',')).join('\n')], { type: 'text/csv' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = `${file.fileName.replace(/\.[^.]+$/, '')}-not-imported.csv`;
    a.click();
    URL.revokeObjectURL(a.href);
  };

  const reset = () => {
    setFile(null);
    setResult(null);
    setReadError(null);
  };

  const skippedCount = rows.length - counts.ready;
  const headerOptions = (file?.headers ?? []).map((h) => ({ value: h, label: h }));
  const shown = filter === 'all' ? rows : rows.filter((r) => r.status === filter);

  return (
    <div className="page">
      <PageHeader title="Notes importer" subtitle="Add notes to many contacts at once from a spreadsheet, and save each one's last communication date." />

      {result ? (
        <Card>
          <Result
            status="success"
            title={`Added ${plural(result.added, 'note')}`}
            subTitle={[
              result.duplicates ? `${plural(result.duplicates, 'note')} skipped because the contact already had them` : null,
              skippedCount ? `${plural(skippedCount, 'row')} not imported` : null,
            ]
              .filter(Boolean)
              .join(' · ')}
            extra={[
              <Link key="open" to="/contacts">
                <Button type="primary">Open contacts</Button>
              </Link>,
              skippedCount ? (
                <Button key="skipped" icon={<DownloadOutlined />} onClick={downloadSkipped}>
                  Download rows not imported
                </Button>
              ) : null,
              <Button key="again" onClick={reset}>
                Import another file
              </Button>,
            ]}
          />
        </Card>
      ) : !file ? (
        <Card>
          <Upload.Dragger
            accept=".xlsx,.csv"
            showUploadList={false}
            disabled={reading}
            beforeUpload={(f) => {
              void read(f);
              return false;
            }}
          >
            <p className="ant-upload-drag-icon">
              <InboxOutlined />
            </p>
            <p className="ant-upload-text">{reading ? 'Reading the file…' : 'Drop a spreadsheet here, or click to choose it'}</p>
            <p className="ant-upload-hint">
              .xlsx or .csv, one row per note: the contact's email, the note (e.g. a summary of what was discussed) and optionally the last communication
              date. Only the first sheet of a workbook is read.
            </p>
          </Upload.Dragger>
          {readError && <Alert type="error" showIcon style={{ marginTop: 16 }} message="Could not read the file" description={readError} />}
        </Card>
      ) : (
        <Space direction="vertical" size={16} style={{ width: '100%' }}>
          <Row gutter={[16, 16]}>
            <Col xs={24} sm={12} lg={6}>
              <StatCard icon={<FileTextOutlined />} label="Notes to add" value={counts.ready} note={`of ${plural(rows.length, 'row')}`} />
            </Col>
            <Col xs={24} sm={12} lg={6}>
              <StatCard icon={<TeamOutlined />} label="Contacts" value={counts.contacts} note="matched by email" />
            </Col>
            <Col xs={24} sm={12} lg={6}>
              <StatCard icon={<CalendarOutlined />} label="With a date" value={counts.dated} note={counts.badDates ? `${counts.badDates} dates not understood` : 'last communication'} />
            </Col>
            <Col xs={24} sm={12} lg={6}>
              <StatCard icon={<UserDeleteOutlined />} label="Not imported" value={skippedCount} note={`${counts['not-found']} no contact · ${counts['no-email'] + counts.empty} blank`} />
            </Col>
          </Row>

          {file.truncated && <Alert type="warning" showIcon message={`Only the first ${plural(file.rows.length, 'row')} of this file are read. Split it to import the rest.`} />}

          <Card title={<CardTitle title="Columns" subtitle={`${file.fileName} · which columns hold what. Extra columns are added under the note as "Column: value" lines.`} />}>
            <Form layout="vertical">
              <Row gutter={16}>
                <Col xs={24} md={8}>
                  <Form.Item label="Email" required help="Matched against every email a contact has">
                    <Select value={columns.email} onChange={(v) => void setEmailColumn(v ?? null)} options={headerOptions} allowClear placeholder="Choose a column" />
                  </Form.Item>
                </Col>
                <Col xs={24} md={8}>
                  <Form.Item label="Note" required>
                    <Select
                      value={columns.note}
                      onChange={(v) => setColumns({ ...columns, note: v ?? null, extras: columns.extras.filter((h) => h !== v) })}
                      options={headerOptions}
                      allowClear
                      placeholder="Choose a column"
                    />
                  </Form.Item>
                </Col>
                <Col xs={24} md={8}>
                  <Form.Item label="Last communication date" help="The note's date and the contact's Last contacted">
                    <Select
                      value={columns.date}
                      onChange={(v) => setColumns({ ...columns, date: v ?? null, extras: columns.extras.filter((h) => h !== v) })}
                      options={headerOptions}
                      allowClear
                      placeholder="None: notes are dated today"
                    />
                  </Form.Item>
                </Col>
              </Row>
              <Form.Item label="Also add to the note">
                <Select
                  mode="multiple"
                  value={columns.extras}
                  onChange={(extras) => setColumns({ ...columns, extras: file.headers.filter((h) => extras.includes(h)) })}
                  options={headerOptions.filter((o) => o.value !== columns.email && o.value !== columns.note && o.value !== columns.date)}
                  placeholder="No extra columns"
                />
              </Form.Item>
              <Checkbox checked={updateLastContacted} onChange={(e) => setUpdateLastContacted(e.target.checked)} disabled={!columns.date}>
                Save the date as each contact's Last contacted (only when it is later than the one they have)
              </Checkbox>
            </Form>
          </Card>

          <Card
            title={<CardTitle title="Preview" subtitle="What each row becomes." />}
            extra={
              <Segmented
                value={filter}
                onChange={(v) => setFilter(v as typeof filter)}
                options={[
                  { value: 'all', label: `All ${rows.length}` },
                  { value: 'ready', label: `Ready ${counts.ready}` },
                  { value: 'not-found', label: `No contact ${counts['not-found']}` },
                ]}
              />
            }
          >
            <Table<PreviewRow>
              rowKey="key"
              size="small"
              dataSource={shown}
              pagination={{ pageSize: 20, showSizeChanger: false, hideOnSinglePage: true }}
              scroll={{ x: 900 }}
              columns={[
                { title: 'Row', dataIndex: 'line', width: 64 },
                {
                  title: 'Contact',
                  width: 240,
                  render: (_, r) =>
                    r.contact ? (
                      <>
                        <Link to={`/contacts/${r.contact.contactId}`}>{r.contact.name}</Link>
                        <Typography.Text type="secondary" style={{ display: 'block', fontSize: 12 }}>
                          {r.email}
                        </Typography.Text>
                      </>
                    ) : (
                      <Typography.Text type="secondary">{r.email || '—'}</Typography.Text>
                    ),
                },
                {
                  title: 'Date',
                  width: 130,
                  render: (_, r) => (r.occurredAt ? fmtDate(r.occurredAt) : r.rawDate ? <Typography.Text type="warning">{r.rawDate}?</Typography.Text> : <Typography.Text type="secondary">Today</Typography.Text>),
                },
                {
                  title: 'Note',
                  render: (_, r) => (
                    <Typography.Paragraph style={{ whiteSpace: 'pre-wrap', margin: 0 }} ellipsis={{ rows: 3, expandable: 'collapsible' }}>
                      {r.body || '—'}
                    </Typography.Paragraph>
                  ),
                },
                { title: 'Status', width: 110, render: (_, r) => <Tag color={STATUS[r.status].color}>{STATUS[r.status].label}</Tag> },
              ]}
            />
          </Card>

          <Card title={<CardTitle title="Import" />}>
            {!columns.email || !columns.note ? (
              <Alert type="info" showIcon message="Choose the Email and Note columns first." />
            ) : (
              <Typography.Paragraph type="secondary" style={{ marginBottom: 0 }}>
                Adds {plural(counts.ready, 'note')} to {plural(counts.contacts, 'contact')}. Notes a contact already has with exactly the same text are skipped,
                so importing the same file again adds nothing. Rows without a matching contact are left out; you can download them after the import.
              </Typography.Paragraph>
            )}
            {progress && <Progress percent={Math.round((progress.done / Math.max(progress.total, 1)) * 100)} style={{ marginTop: 16 }} />}
            <div style={{ display: 'flex', gap: 8, marginTop: 16, flexWrap: 'wrap' }}>
              <Button type="primary" onClick={() => void run()} loading={!!progress} disabled={!counts.ready || !columns.note}>
                Add {plural(counts.ready, 'note')}
              </Button>
              {skippedCount > 0 && (
                <Button icon={<DownloadOutlined />} onClick={downloadSkipped} disabled={!!progress}>
                  Download {plural(skippedCount, 'row')} not imported
                </Button>
              )}
              <Button onClick={reset} disabled={!!progress}>
                Choose another file
              </Button>
            </div>
          </Card>
        </Space>
      )}
    </div>
  );
}
